import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Photo from "@/components/Photo";
import Reveal from "@/components/Reveal";
import FaqAccordion from "@/components/FaqAccordion";
import JsonLd from "@/components/JsonLd";
import { DoctolibLogo } from "@/components/DoctolibLogo";
import useSeo, { SITE_URL } from "@/hooks/useSeo";
import { SITE } from "@/constants/site";
import useI18n from "@/i18n";
import { HTML_LANG, pagePath, soinKeyFromSlug, soinPath } from "@/i18n/config";

const structuredData = ({ page, url, home, lang, navLabel }) => ({
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "MedicalWebPage",
            name: page.title,
            description: page.description,
            url,
            inLanguage: HTML_LANG[lang],
            about: { "@type": "MedicalCondition", name: page.condition },
            audience: { "@type": "Patient" },
            lastReviewed: "2026-07-10",
            provider: {
                "@type": "MedicalClinic",
                name: SITE.name,
                url: `${SITE_URL}${home}`,
                address: {
                    "@type": "PostalAddress",
                    streetAddress: "10 rue Vladimir Kramnik",
                    postalCode: "92600",
                    addressLocality: "Asnières-sur-Seine",
                    addressCountry: "FR",
                },
            },
        },
        {
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: navLabel, item: `${SITE_URL}${home}` },
                { "@type": "ListItem", position: 2, name: page.nav, item: url },
            ],
        },
        {
            "@type": "FAQPage",
            mainEntity: page.faq.map(([q, a]) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
            })),
        },
    ],
});

export default function SoinPage() {
    const { slug } = useParams();
    const { t, soins, lang } = useI18n();
    const key = soinKeyFromSlug(lang, slug);
    const page = key ? soins[key] : null;
    const path = key ? soinPath(lang, key) : `/${slug}`;

    useSeo({
        title: page ? page.title : t.seo.notfound.title,
        description: page ? page.description : "",
        path,
        lang,
        soinKey: key || undefined,
        noindex: !page,
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!page) return <Navigate to={pagePath(lang, "notfound")} replace />;

    return (
        <>
            <JsonLd
                id={`ld-${key}`}
                data={structuredData({
                    page,
                    url: `${SITE_URL}${path}`,
                    home: pagePath(lang, "home"),
                    lang,
                    navLabel: t.common.home,
                })}
            />
            <Header />
            <main className="pt-24" data-testid={`soin-page-${key}`}>
                <div className="mx-auto max-w-[840px] px-5 pb-20 pt-6">
                    <nav aria-label={t.common.breadcrumbAria} className="text-sm text-ink2">
                        <Link
                            to={pagePath(lang, "home")}
                            className="inline-flex items-center gap-1.5 font-semibold text-terrat transition-colors duration-200 hover:text-terrad"
                            data-testid="breadcrumb-home"
                        >
                            <ArrowLeft size={15} aria-hidden="true" />
                            {t.common.home}
                        </Link>
                        <span aria-hidden="true" className="px-2">·</span>
                        <span>{page.nav}</span>
                    </nav>

                    <Reveal>
                        <p className="eyebrow mt-8">{page.eyebrow}</p>
                        <h1 className="title-grad mt-4 text-4xl sm:text-5xl">{page.h1}</h1>
                        <hr className="rule-gold mt-5 w-24" />
                        <p className="mt-5 max-w-[62ch] text-lg text-ink2">{page.lead}</p>
                    </Reveal>

                    <Reveal delay={0.08}>
                        <Photo
                            file={page.photo.file}
                            alt={page.photo.alt}
                            ratioClass="aspect-[16/9] rounded-[14px]"
                            className="mt-10"
                        />
                    </Reveal>

                    <div className="mt-14 space-y-12">
                        {page.sections.map((s, i) => (
                            <Reveal key={s.h2}>
                                <section data-testid={`soin-section-${i + 1}`}>
                                    <h2 className="text-2xl text-terrat md:text-3xl">{s.h2}</h2>
                                    <hr className="rule-gold mt-3 w-12" />
                                    <div className="mt-4 space-y-4 text-ink2">
                                        {s.paragraphes.map((p) => (
                                            <p key={p.slice(0, 40)}>{p}</p>
                                        ))}
                                    </div>
                                    {s.puces && (
                                        <ul className="mt-5 space-y-2">
                                            {s.puces.map((item) => (
                                                <li key={item} className="flex gap-2.5 text-ink2">
                                                    <span aria-hidden="true" style={{ color: "var(--c-terra)" }}>·</span>
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </section>
                            </Reveal>
                        ))}
                    </div>

                    <div className="mt-16">
                        <Reveal>
                            <h2 className="text-2xl text-terrat md:text-3xl">
                                {t.pages.soin.faqTitle}
                            </h2>
                            <hr className="rule-gold mt-3 w-12" />
                        </Reveal>
                        <div className="mt-6">
                            <FaqAccordion items={page.faq} prefix={`faq-${key}`} />
                        </div>
                    </div>

                    <Reveal>
                        <div className="card mt-16 p-7" data-testid="soin-cta">
                            <h2 className="text-2xl text-terrat">{t.pages.soin.ctaTitle}</h2>
                            <hr className="rule-gold mt-3 w-12" />
                            <p className="mt-4 text-ink2">{t.pages.soin.ctaText}</p>
                            <a
                                href={SITE.doctolibUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary mt-6"
                                data-testid="soin-rdv-button"
                            >
                                {t.common.rdvOn}
                                <DoctolibLogo height={15} color="#FFFFFF" />
                            </a>
                        </div>
                    </Reveal>

                    <Reveal>
                        <Link
                            to={pagePath(lang, "conseils")}
                            className="link-underline mt-8 inline-block text-sm font-bold text-terrat"
                            data-testid="soin-link-conseils"
                        >
                            {t.common.conseilsLink}
                        </Link>
                    </Reveal>

                    <Reveal>
                        <div className="mt-14">
                            <h2 className="text-xl text-olivet">{t.pages.soin.relatedTitle}</h2>
                            <hr className="rule-gold mt-3 w-12" />
                            <ul className="mt-4 space-y-2">
                                {page.related.map((k) => (
                                    <li key={k}>
                                        <Link
                                            to={soinPath(lang, k)}
                                            className="link-underline inline-flex items-center gap-2 font-semibold text-terrat"
                                            data-testid={`related-${k}`}
                                        >
                                            {soins[k].h1}
                                            <ArrowRight size={15} aria-hidden="true" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>

                    <Reveal>
                        <p className="mt-12 max-w-[72ch] text-sm italic text-ink2">
                            {t.pages.soin.disclaimer}
                        </p>
                    </Reveal>
                </div>
            </main>
            <Footer />
        </>
    );
}
